import { useId } from 'react'
import { Autocomplete, Box, MenuItem, Stack, TextField, Typography } from '@mui/material'
import type { TipoDestinoBanner } from '../../api'
import { fimAntesDoInicio, type BannerRascunho, type DestinoBannerRascunho } from '../../lib/banners'
import { UploadImagem } from './upload-imagem'

export interface OpcaoProduto {
  id: number
  descricao: string
  pausado: boolean
  oculto: boolean
  grupo: string
}

export interface OpcaoGrupo {
  id: number
  descricao: string
  oculto: boolean
}

interface PainelBannerProps {
  banner: BannerRascunho
  hoje: string
  produtos: OpcaoProduto[]
  grupos: OpcaoGrupo[]
  produtoEscolhido: OpcaoProduto | null
  grupoEscolhido: OpcaoGrupo | null
  catalogoCarregado: boolean
  carregando: boolean
  aoAlterar: (parcial: Partial<BannerRascunho>) => void
}

const OPCOES_DESTINO: Array<{ tipo: TipoDestinoBanner; rotulo: string }> = [
  { tipo: 'NENHUM', rotulo: 'Nada (só mostra a imagem)' },
  { tipo: 'PRODUTO', rotulo: 'Um produto' },
  { tipo: 'GRUPO', rotulo: 'Um grupo de produtos' },
  { tipo: 'PROMOCOES', rotulo: 'A lista de promoções' },
]

const DATAS_INVERTIDAS = 'O "Mostrar até" está antes do "Mostrar de". Corrija uma das datas.'

/** Trocar o tipo começa um destino novo: o id de um produto não serve de grupo. */
function destinoVazio(tipo: TipoDestinoBanner): DestinoBannerRascunho {
  switch (tipo) {
    case 'PRODUTO':
      return { tipo, idProduto: null }
    case 'GRUPO':
      return { tipo, idGrupo: null }
    case 'PROMOCOES':
      return { tipo }
    default:
      return { tipo: 'NENHUM' }
  }
}

function comMarcas(descricao: string, marcas: Array<string | false>): string {
  const ativas = marcas.filter((marca): marca is string => Boolean(marca))
  return ativas.length > 0 ? `${descricao} (${ativas.join(', ')})` : descricao
}

export function rotuloProduto(produto: OpcaoProduto): string {
  return comMarcas(produto.descricao, [produto.pausado && 'pausado', produto.oculto && 'oculto'])
}

export function rotuloGrupo(grupo: OpcaoGrupo): string {
  return comMarcas(grupo.descricao, [grupo.oculto && 'oculto'])
}

function ajudaProduto(sumiu: boolean, semEscolha: boolean, escolhido: OpcaoProduto | null): string | undefined {
  if (sumiu) return 'O produto escolhido não está mais no cadastro. Escolha outro.'
  if (semEscolha) return 'Escolha o produto que o banner abre.'
  if (escolhido?.oculto) return 'Este produto está oculto em Visibilidade dos itens: o banner não aparece.'
  if (escolhido?.pausado) return 'Este produto está pausado: o banner só aparece quando ele voltar ao cardápio.'
  return undefined
}

function ajudaGrupo(sumiu: boolean, semEscolha: boolean, escolhido: OpcaoGrupo | null): string {
  if (sumiu) return 'O grupo escolhido não está mais no cadastro. Escolha outro.'
  if (semEscolha) return 'Escolha o grupo que o banner abre.'
  if (escolhido?.oculto) return 'Este grupo está oculto em Visibilidade dos itens: o banner não aparece.'
  return 'Se este grupo estiver oculto em Visibilidade dos itens, o banner não aparece.'
}

/** Painel aberto de um banner: imagem e campos, editados ali mesmo. */
export function PainelBanner({
  banner,
  hoje,
  produtos,
  grupos,
  produtoEscolhido,
  grupoEscolhido,
  catalogoCarregado,
  carregando,
  aoAlterar,
}: PainelBannerProps): JSX.Element {
  const id = useId()
  const destino = banner.destino
  // Escolha salva que sumiu do cadastro: o cliente não veria o banner, então o lojista precisa saber.
  const produtoSumiu = catalogoCarregado && destino.tipo === 'PRODUTO' && destino.idProduto !== null && !produtoEscolhido
  const grupoSumiu = catalogoCarregado && destino.tipo === 'GRUPO' && destino.idGrupo !== null && !grupoEscolhido
  const datasInvertidas = fimAntesDoInicio(banner)
  const ajudaInicio = banner.inicio && banner.inicio > hoje ? 'O banner começa a aparecer neste dia.' : undefined
  const ajudaFim = datasInvertidas
    ? DATAS_INVERTIDAS
    : banner.fim && banner.fim < hoje
      ? 'Esta data já passou: o banner não aparece mais.'
      : undefined

  // Passar o `aria-describedby` tira o do MUI, então o texto de ajuda do campo entra junto com a legenda.
  function descricaoDaData(idCampo: string, ajuda: string | undefined): string {
    return ajuda ? `${idCampo}-helper-text ${id}-legenda` : `${id}-legenda`
  }

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ px: 2, pt: 1, pb: 2 }}>
      <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
        <UploadImagem
          variant="banner"
          finalidade="capa"
          valor={banner.urlImagem}
          removivel={false}
          onChange={(url) => {
            if (url) aoAlterar({ urlImagem: url })
          }}
        />
      </Box>

      <Stack spacing={2} sx={{ flexGrow: 1, minWidth: 0 }}>
        <TextField
          label="Nome do banner (opcional)"
          fullWidth
          value={banner.nome ?? ''}
          onChange={(e) => aoAlterar({ nome: e.target.value || null })}
          helperText='Não sai no cardápio: serve para você achar o banner nesta lista e é o que o leitor de tela lê. Ex.: "Fechado no dia 25".'
          slotProps={{ htmlInput: { maxLength: 80 } }}
        />

        <TextField
          select
          label="Quando o cliente tocar no banner, abrir"
          fullWidth
          value={destino.tipo}
          onChange={(e) => {
            const tipo = e.target.value as TipoDestinoBanner
            if (tipo !== destino.tipo) aoAlterar({ destino: destinoVazio(tipo) })
          }}
          helperText={
            destino.tipo === 'PROMOCOES' ? 'Só aparece enquanto houver algum item com preço especial no cardápio.' : undefined
          }
        >
          {OPCOES_DESTINO.map((opcao) => (
            <MenuItem key={opcao.tipo} value={opcao.tipo}>
              {opcao.rotulo}
            </MenuItem>
          ))}
        </TextField>

        {destino.tipo === 'PRODUTO' && (
          <Autocomplete
            options={produtos}
            value={produtoEscolhido}
            onChange={(_e, opcao) => aoAlterar({ destino: { tipo: 'PRODUTO', idProduto: opcao?.id ?? null } })}
            groupBy={(opcao) => opcao.grupo}
            getOptionLabel={rotuloProduto}
            isOptionEqualToValue={(opcao, atual) => opcao.id === atual.id}
            loading={carregando}
            loadingText="Carregando produtos..."
            noOptionsText="Nenhum produto encontrado"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Produto"
                error={produtoSumiu}
                helperText={ajudaProduto(produtoSumiu, destino.idProduto === null, produtoEscolhido)}
              />
            )}
          />
        )}

        {destino.tipo === 'GRUPO' && (
          <Autocomplete
            options={grupos}
            value={grupoEscolhido}
            onChange={(_e, opcao) => aoAlterar({ destino: { tipo: 'GRUPO', idGrupo: opcao?.id ?? null } })}
            getOptionLabel={rotuloGrupo}
            isOptionEqualToValue={(opcao, atual) => opcao.id === atual.id}
            loading={carregando}
            loadingText="Carregando grupos..."
            noOptionsText="Nenhum grupo encontrado"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Grupo"
                error={grupoSumiu}
                helperText={ajudaGrupo(grupoSumiu, destino.idGrupo === null, grupoEscolhido)}
              />
            )}
          />
        )}

        <Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              id={`${id}-inicio`}
              type="date"
              label="Mostrar de"
              fullWidth
              value={banner.inicio ?? ''}
              onChange={(e) => aoAlterar({ inicio: e.target.value || null })}
              helperText={ajudaInicio}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { 'aria-describedby': descricaoDaData(`${id}-inicio`, ajudaInicio) },
              }}
            />
            <TextField
              id={`${id}-fim`}
              type="date"
              label="Mostrar até"
              fullWidth
              value={banner.fim ?? ''}
              onChange={(e) => aoAlterar({ fim: e.target.value || null })}
              error={datasInvertidas}
              helperText={ajudaFim}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { min: banner.inicio ?? undefined, 'aria-describedby': descricaoDaData(`${id}-fim`, ajudaFim) },
              }}
            />
          </Stack>
          <Typography id={`${id}-legenda`} variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, mx: 1.75 }}>
            Em branco: começa já e não tem fim.
          </Typography>
        </Box>
      </Stack>
    </Stack>
  )
}
